import type { MetaType, MetaTypeId, ApproachAxis, ActionAxis } from '../types/richPersona';

// ─────────────────────────────────────────────────
// 4 MetaTypes — 2×2 Matrix
// X축: data(데이터/계획) ↔ emotion(감정/상태)
// Y축: action(빠른 행동) ↔ reflection(깊은 성찰)
// ─────────────────────────────────────────────────

export const META_TYPE_MATRIX: Record<ApproachAxis, Record<ActionAxis, MetaTypeId>> = {
  data:    { action: 'stopwatch', reflection: 'blueprint' },
  emotion: { action: 'campfire',  reflection: 'compass' },
};

export const META_TYPES: Record<MetaTypeId, MetaType> = {
  stopwatch: {
    id: 'stopwatch',
    emoji: '⏱️',
    nameKo: '스톱워치',
    typeName: '실행가',
    story: '타이머를 세팅하고, 집중하고, 완료한다. 측정 가능한 목표와 빠른 실행을 좋아하는 당신은 데이터와 행동의 달인이에요.',
    approachAxis: 'data',
    actionAxis: 'action',
    strategies: [
      '타이머를 활용한 스프린트 집중으로 과제를 빠르게 완료하기',
      '체크리스트를 만들고 하나씩 완료 표시하며 진행하기',
      '목표를 숫자로 설정하고 달성률을 추적하기',
      '완료한 일을 정리하며 다음 액션을 바로 세팅하기',
    ],
    dailyRoutine: {
      morning: '오늘의 목표 3가지를 구체적으로 설정하고 타이머 준비하기',
      focus: '스프린트 타이머와 함께 한 가지에 레이저 집중하기',
      lunch: '오전 진행률 체크하고 오후 스프린트 계획 조정하기',
      evening: '완료한 항목을 정리하고 내일의 첫 번째 액션 세팅하기',
      growth: '오늘 달성한 것을 돌아보며 "한 걸음 더 나아갔다"를 확인하기',
    },
    encouragements: [
      '오늘도 목표를 향해 한 걸음 더 나아갔어요!',
      '빠른 실행력이 빛나는 하루였어요!',
      '완료 표시를 하나 더 찍었네요. 멈추지 않는 실행가!',
    ],
    coachingTone: {
      style: '직접적이고 결과 지향적, 간결한 피드백',
      keywords: ['실행', '완료', '달성', '목표', '속도'],
    },
  },

  blueprint: {
    id: 'blueprint',
    emoji: '📐',
    nameKo: '블루프린트',
    typeName: '설계가',
    story: '계획을 세우고, 분석하고, 패턴을 찾는다. 구조와 데이터를 사랑하는 당신은 깊은 성찰로 최적의 전략을 설계해요.',
    approachAxis: 'data',
    actionAxis: 'reflection',
    strategies: [
      '주간 리포트를 꼼꼼히 확인하며 패턴과 트렌드 분석하기',
      '목표를 구조적으로 분류하고 우선순위 매트릭스 활용하기',
      '활동 데이터를 기록하고 주기적으로 리뷰하기',
      '전체 구조를 파악한 뒤 단계별 전략 수립하기',
    ],
    dailyRoutine: {
      morning: '전체 일정과 구조를 파악하고 전략적으로 우선순위 배치하기',
      focus: '체계적으로 분석하며 핵심 개념을 구조화하기',
      lunch: '오전 진행 상황을 분석하고 오후 전략 최적화하기',
      evening: '하루의 데이터를 리뷰하고 패턴을 기록하기',
      growth: '오늘의 분석 중 가장 의미 있는 발견을 정리하기',
    },
    encouragements: [
      '분석이 깊어질수록 다음 전략이 더 정확해져요.',
      '체계적인 접근이 빛나는 하루였어요!',
      '패턴을 읽는 눈이 점점 날카로워지고 있어요.',
    ],
    coachingTone: {
      style: '분석적이고 체계적, 데이터 기반 피드백',
      keywords: ['분석', '패턴', '구조', '전략', '최적화'],
    },
  },

  campfire: {
    id: 'campfire',
    emoji: '🔥',
    nameKo: '모닥불',
    typeName: '촉진자',
    story: '에너지와 감정을 원동력으로 즉시 행동한다. 따뜻한 불꽃처럼 주변을 이끄는 당신은 감정과 행동의 촉진자예요.',
    approachAxis: 'emotion',
    actionAxis: 'action',
    strategies: [
      '에너지 레벨에 맞춰 활동 강도를 조절하며 실행하기',
      '감정 체크인으로 오늘의 컨디션을 파악하고 맞춤 계획 세우기',
      '성장 기록을 활발하게 남기며 감정 기반 동기 관리하기',
      '주변 사람들과 에너지를 나누며 함께 행동하기',
    ],
    dailyRoutine: {
      morning: '에너지 체크 — 오늘 기분에 맞는 첫 번째 활동 선택하기',
      focus: '감정 에너지를 실행으로 전환! 기분 좋은 일부터 시작하기',
      lunch: '에너지 재충전 — 감정 상태를 돌아보고 오후 활력 채우기',
      evening: '오늘의 감정 흐름을 회고하고 의미 있는 순간 기록하기',
      growth: '오늘의 에너지를 잘 활용했는지 돌아보며 내일 의지 다짐하기',
    },
    encouragements: [
      '오늘의 에너지를 잘 활용했어요! 내일도 활활!',
      '따뜻한 에너지로 주변을 밝힌 하루였어요!',
      '감정을 행동으로 바꾸는 힘이 대단해요!',
    ],
    coachingTone: {
      style: '따뜻하고 에너지 넘침, 감정 공감 + 행동 유도',
      keywords: ['에너지', '감정', '함께', '행동', '활력'],
    },
  },

  compass: {
    id: 'compass',
    emoji: '🧭',
    nameKo: '나침반',
    typeName: '탐구자',
    story: '내면을 탐색하고 의미를 찾는다. 깊이 있는 성찰을 즐기는 당신은 방향을 찾아가는 나침반 같은 존재예요.',
    approachAxis: 'emotion',
    actionAxis: 'reflection',
    strategies: [
      '성장 기록을 깊이 있게 작성하며 의미와 패턴 발견하기',
      '성찰 기반으로 목표를 설정하고 내면의 동기 탐색하기',
      '코칭 대화를 통해 자신의 생각과 감정을 탐구하기',
      '질문을 던지며 새로운 관점과 방향 발견하기',
    ],
    dailyRoutine: {
      morning: '오늘의 의미 찾기 — 이 하루가 나에게 어떤 의미일지 생각하기',
      focus: '탐구적 학습 — 질문을 던지며 깊이 파고들기',
      lunch: '오전의 발견을 기록하고 새로운 질문 떠올리기',
      evening: '깊은 성찰 — 오늘의 경험에서 의미 있는 것 찾기',
      growth: '내면 대화 — 오늘 나는 어떤 방향으로 성장했는지 돌아보기',
    },
    encouragements: [
      '깊이 있는 성찰이 방향을 더 선명하게 만들어줘요.',
      '내면을 탐색하는 용기가 빛나는 하루였어요!',
      '의미를 찾아가는 여정이 점점 깊어지고 있어요.',
    ],
    coachingTone: {
      style: '질문 중심, 탐색적, 의미 부여',
      keywords: ['의미', '성찰', '탐구', '방향', '발견'],
    },
  },
};

/** Look up MetaType by approach + action axes */
export function getMetaType(approach: ApproachAxis, action: ActionAxis): MetaType {
  const id = META_TYPE_MATRIX[approach][action];
  return META_TYPES[id];
}

/** Get MetaType by ID */
export function getMetaTypeById(id: MetaTypeId): MetaType {
  return META_TYPES[id];
}
